import { useState } from 'react';
import { Link, ExternalLink, AlertTriangle, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { profileApi } from '@/lib/api';

interface TradeUrlRequiredDialogProps {
  open: boolean;
  onClose: () => void;
  /** Вызывается после успешного сохранения trade URL — используйте для продолжения покупки */
  onSaved: () => void;
}

export function TradeUrlRequiredDialog({ open, onClose, onSaved }: TradeUrlRequiredDialogProps) {
  const [tradeUrl, setTradeUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const isValid = tradeUrl.startsWith('https://steamcommunity.com/tradeoffer/new/');

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      await profileApi.updateProfile({ steam_trade_url: tradeUrl });
      toast.success('Trade URL сохранён');
      onClose();
      onSaved();
    } catch (err: any) {
      toast.error('Не удалось сохранить Trade URL: ' + (err.message || 'Неизвестная ошибка'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-[#161b22] border-[#30363d] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <Link className="w-5 h-5 text-[#ff6b00]" />
            Укажите Trade URL
          </DialogTitle>
          <DialogDescription className="text-[#8b949e]">
            Для получения купленного предмета необходимо указать вашу ссылку для обмена Steam
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <Input
              value={tradeUrl}
              onChange={(e) => setTradeUrl(e.target.value)}
              placeholder="https://steamcommunity.com/tradeoffer/new/?partner=...&token=..."
              className="bg-[#0d1117] border-[#30363d] text-white placeholder:text-[#8b949e] focus:border-[#ff6b00]"
              autoFocus
            />
            {tradeUrl && !isValid && (
              <p className="text-red-400 text-xs flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Неверный формат Trade URL
              </p>
            )}
          </div>

          <a
            href="https://steamcommunity.com/id/me/tradeoffers/privacy#trade_offer_access_url"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-[#66c0f4] hover:underline flex items-center gap-1"
          >
            Где взять Trade URL?
            <ExternalLink className="w-3 h-3" />
          </a>

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-[#30363d] text-[#8b949e] hover:bg-[#30363d]"
              disabled={saving}
            >
              Отмена
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isValid || saving}
              className="flex-1 bg-[#ff6b00] hover:bg-[#ff8533] text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Сохранение...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Сохранить и оплатить
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
